<script setup lang="ts">
import { NATIVE_STORE_URL } from '~/utils/clientConfig'

defineProps<{ checking: boolean }>()
const emit = defineEmits<{ retry: [] }>()
const { t } = useLocale()
const title = ref<HTMLElement | null>(null)

onMounted(() => title.value?.focus({ preventScroll: true }))
</script>

<template>
  <main class="update-required" aria-labelledby="update-required-title" :aria-busy="checking" data-testid="update-required">
    <div class="update-content">
      <img class="update-logo" src="/logo.webp" :alt="t('updateRequired.gameTitle')" width="720" height="615">
      <section class="card update-card" aria-describedby="update-required-body">
        <h1 id="update-required-title" ref="title" tabindex="-1">{{ t('updateRequired.title') }}</h1>
        <p id="update-required-body" class="update-body">{{ t('updateRequired.body') }}</p>
        <div class="update-actions">
          <a class="btn btn-bar update-store" :href="NATIVE_STORE_URL" data-testid="update-store">{{ t('updateRequired.store') }}</a>
          <button class="btn btn-frame-white" type="button" :disabled="checking" data-testid="update-retry" @click="emit('retry')">{{ t('updateRequired.retry') }}</button>
        </div>
        <p class="update-status" role="status" aria-live="polite">{{ checking ? t('updateRequired.checking') : '' }}</p>
      </section>
    </div>
  </main>
</template>

<style scoped>
.update-required {
  position: fixed;
  inset: 0;
  display: flex;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: max(20px, var(--safe-top)) max(16px, var(--safe-right)) max(20px, var(--safe-bottom)) max(16px, var(--safe-left));
  background: linear-gradient(rgba(31, 58, 95, 0.12), rgba(12, 26, 48, 0.45)), url('/ui/backdrop.webp') center bottom / cover no-repeat, var(--color-sky-top);
}
.update-content {
  width: min(440px, 100%);
  margin: auto;
  text-align: center;
}
.update-logo {
  display: block;
  width: 150px;
  max-width: 60%;
  height: auto;
  margin: 0 auto 14px;
  filter: drop-shadow(0 4px 8px rgba(12, 26, 48, 0.35));
}
.update-card { padding: 18px 14px 8px; }
.update-card h1 {
  margin: 0 0 8px;
  font-size: clamp(1.65rem, 6vw, 2.1rem);
  line-height: 1.15;
}
.update-card h1:focus { outline: none; }
.update-body {
  max-width: 31ch;
  margin: 0 auto 18px;
  line-height: 1.5;
}
.update-actions { display: grid; gap: 8px; }
.update-store {
  /* Seat the label on the gold face above the frame's lower gem. */
  padding: 0 26px 18px;
  text-decoration: none;
}
.update-actions :focus-visible {
  outline: 3px solid var(--color-ink);
  outline-offset: 3px;
}
.update-actions button:disabled {
  cursor: wait;
  opacity: 0.65;
}
.update-status {
  min-height: 1.4em;
  margin: 8px 0 0;
  color: var(--color-ink-soft);
  font-size: 0.875rem;
}
@media (max-height: 560px) {
  .update-logo { width: 90px; margin-bottom: 8px; }
  .update-card { padding-top: 10px; }
  .update-body { margin-bottom: 10px; }
}
</style>
